import React from "react";
import { Bell, X, LayoutGrid, Copy, Trash2, Plus, Rocket, Settings, Download, LogOut, Shield } from "lucide-react";
import { T, PLAN_LABELS } from "../theme";
import { LogoMark } from "./shared";

export default function Sidebar({ user, profiles, activeProfile, viewAll, alerts, showNotifs, setShowNotifs,
  sidebarOpen, setSidebarOpen, onSelectProfile, onViewAll, onNewProfile, onDuplicate, onRemove,
  onQuickStart, onSettings, onExport, onLogout, addProfileError, isMaster, onAdmin }) {

  const close = () => setSidebarOpen(false);

  return (
    <div className={`no-print sidebar ${sidebarOpen ? "open" : ""}`} data-testid="sidebar" style={{ width: 216, borderRight: `1px solid ${T.line}`, padding: "20px 14px", flexShrink: 0, display: "flex", flexDirection: "column", background: T.bg, overflowY: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LogoMark size={18} />
          <span style={{ fontFamily: T.fontHead, fontSize: 17 }}>So.Help</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <div style={{ position: "relative" }}>
            <button data-testid="sidebar-notifications-btn" onClick={() => setShowNotifs((o) => !o)} title="Notifiche" style={{ background: "transparent", border: "none", color: alerts.length ? T.amber : T.textDim, cursor: "pointer", padding: 4, position: "relative" }}>
              <Bell size={16} />
              {alerts.length > 0 && <span style={{ position: "absolute", top: 2, right: 2, width: 6, height: 6, borderRadius: "50%", background: T.rose }} />}
            </button>
            {showNotifs && (
              <div data-testid="sidebar-notifications-panel" style={{ position: "absolute", top: 26, left: 0, width: 240, background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 12, zIndex: 30, boxShadow: "0 8px 24px rgba(30,30,60,0.2)" }}>
                {alerts.length === 0 ? (
                  <div style={{ fontSize: 12, color: T.textDim }}>Nessuna novità al momento.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {alerts.map((a, i) => <div key={i} style={{ fontSize: 12, color: T.text, lineHeight: 1.4 }}>{a}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
          <button className="sidebar-close no-print" onClick={close} style={{ display: "none", background: "transparent", border: "none", color: T.textDim, cursor: "pointer", padding: 4 }}><X size={16} /></button>
        </div>
      </div>
      <div style={{ color: T.textDim, fontSize: 12, marginBottom: 4, wordBreak: "break-all" }}>{user.email}</div>
      <div style={{ color: T.textDim, fontSize: 11, marginBottom: 18 }}>Piano <span data-testid="sidebar-plan-label" style={{ color: T.amber }}>{PLAN_LABELS[user.plan]}</span></div>

      <button data-testid="sidebar-all-profiles-btn" onClick={() => { onViewAll(); close(); }} style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", marginBottom: 14,
        borderRadius: 6, background: viewAll ? T.surfaceAlt : "transparent",
        border: `1px solid ${viewAll ? T.line : "transparent"}`, color: T.text, fontSize: 13, cursor: "pointer", textAlign: "left",
      }}><LayoutGrid size={15} /> Tutti i profili</button>

      <div style={{ color: T.textDim, fontSize: 12, fontWeight: 600, margin: "4px 4px 8px" }}>Profili ({profiles.length})</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 200, overflowY: "auto" }}>
        {profiles.map((p) => (
          <div key={p.id} style={{ display: "flex", alignItems: "center" }}>
            <button data-testid={`sidebar-profile-${p.id}`} onClick={() => { onSelectProfile(p.id); close(); }} style={{
              flex: 1, textAlign: "left", padding: "8px 10px", borderRadius: 6,
              background: !viewAll && activeProfile?.id === p.id ? T.surfaceAlt : "transparent",
              border: "none", color: !viewAll && activeProfile?.id === p.id ? T.text : T.textDim, fontSize: 13, cursor: "pointer",
            }}>{p.name}</button>
            <button data-testid={`sidebar-duplicate-${p.id}`} onClick={() => onDuplicate(p.id)} title="Duplica profilo" style={{ background: "transparent", border: "none", color: T.textDim, cursor: "pointer", padding: 4 }}><Copy size={13} /></button>
            {profiles.length > 1 && (
              <button data-testid={`sidebar-remove-${p.id}`} onClick={() => onRemove(p.id)} title="Rimuovi profilo" style={{ background: "transparent", border: "none", color: T.textDim, cursor: "pointer", padding: 4 }}><Trash2 size={13} /></button>
            )}
          </div>
        ))}
      </div>

      <button data-testid="sidebar-new-profile-btn" onClick={() => { onNewProfile(); close(); }} style={{
        marginTop: 12, display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "8px 10px",
        borderRadius: 6, background: "transparent", border: `1px dashed ${T.line}`, color: T.textDim, fontSize: 13, cursor: "pointer",
      }}><Plus size={14} /> Nuovo profilo</button>
      {addProfileError && <div data-testid="sidebar-plan-limit-error" style={{ color: T.rose, fontSize: 11, marginTop: 8, lineHeight: 1.4 }}>{addProfileError}</div>}
      <button data-testid="sidebar-quickstart-btn" onClick={() => { onQuickStart(); close(); }} style={{
        marginTop: 8, display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "8px 10px",
        borderRadius: 6, background: `${T.viki}14`, border: `1px solid ${T.viki}55`, color: T.viki, fontSize: 13, cursor: "pointer", fontWeight: 600,
      }}><Rocket size={14} /> Configurazione guidata</button>

      <div style={{ flex: 1, minHeight: 20 }} />
      <div style={{ color: T.textDim, fontSize: 12, fontWeight: 600, margin: "4px 4px 8px" }}>Account</div>
      {isMaster && (
        <button data-testid="sidebar-admin-btn" onClick={() => { onAdmin(); close(); }} style={{
          display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none",
          color: T.text, fontSize: 12, cursor: "pointer", padding: "6px 2px", marginBottom: 4, fontWeight: 600,
        }}><Shield size={12} color={T.amber} /> Amministrazione</button>
      )}
      <button data-testid="sidebar-settings-btn" onClick={() => { onSettings(); close(); }} style={{
        display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none",
        color: T.text, fontSize: 12, cursor: "pointer", padding: "6px 2px", marginBottom: 4,
      }}><Settings size={12} color={T.amber} /> Impostazioni account</button>
      <button data-testid="sidebar-export-btn" onClick={() => { onExport(); close(); }} style={{
        display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none",
        color: T.textDim, fontSize: 12, cursor: "pointer", padding: "6px 2px", marginBottom: 4,
      }}><Download size={12} /> Esporta i miei dati</button>
      <div style={{ borderTop: `1px solid ${T.line}`, margin: "6px 2px 8px" }} />
      <button data-testid="sidebar-logout-btn" onClick={() => { onLogout(); close(); }} style={{
        display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none",
        color: T.textDim, fontSize: 12, cursor: "pointer", padding: "6px 2px",
      }}><LogOut size={12} /> Esci</button>
    </div>
  );
}
